import { Link } from 'react-router-dom';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { Box } from '@givebutter/primitives/box';
import { faArrowRight, faCheckCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FTC_CAN_SPAM_INFO_LINK } from 'constants/links';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { EngageMessageChannel } from 'types/domain';

const GettingStarted = ({ channel }: { channel?: EngageMessageChannel }) => {
    const {
        accountId,
        tasks: { engage: tasks },
    } = useCurrentAccount();

    const showEmailRequirements = !channel || channel === 'email';

    return (
        <div className={'grid grid-cols-12 gap-4'}>
            {showEmailRequirements && (
                <>
                    <Box className={'col-span-full p-4 md:col-span-4'}>
                        <div className="flex flex-col gap-2">
                            <p className="font-medium">Email Domain</p>
                            <p className="text-sm">
                                Connect a custom domain to get started sending email.
                            </p>
                            {tasks.has_domain && (
                                <AlertNotification.Root type="success">
                                    <AlertNotification.Content>
                                        <AlertNotification.Title className="flex items-center gap-1.5">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            Successfully linked!
                                        </AlertNotification.Title>
                                    </AlertNotification.Content>
                                </AlertNotification.Root>
                            )}
                            {!tasks.has_domain && (
                                <Link
                                    className={'flex w-fit items-center gap-1.5'}
                                    to={`/accounts/${accountId}/engage/settings/domains`}
                                >
                                    <p>Set up domain</p>
                                    <FontAwesomeIcon icon={faArrowRight} />
                                </Link>
                            )}
                        </div>
                    </Box>

                    <Box className={'col-span-full p-4 md:col-span-4'}>
                        <div className="flex flex-col gap-2">
                            <p className="font-medium">Custom Branding</p>
                            <p className="text-sm">
                                Keep your emails on brand by uploading a logo and choosing a brand
                                color.
                            </p>
                            {tasks.has_branding && (
                                <AlertNotification.Root type="success">
                                    <AlertNotification.Content>
                                        <AlertNotification.Title className="flex items-center gap-1.5">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            Successfully linked!
                                        </AlertNotification.Title>
                                    </AlertNotification.Content>
                                </AlertNotification.Root>
                            )}
                            {!tasks.has_branding && (
                                <Link
                                    className={'flex w-fit items-center gap-1.5'}
                                    to={`/accounts/${accountId}/engage/settings/branding`}
                                >
                                    <p>Set up branding</p>
                                    <FontAwesomeIcon icon={faArrowRight} />
                                </Link>
                            )}
                        </div>
                    </Box>
                </>
            )}
            <Box className={'col-span-full p-4 md:col-span-4'}>
                <div className="flex flex-col gap-2">
                    <p className="font-medium">Organization Address</p>
                    <p className="text-sm">
                        Your address is required by the{' '}
                        <a target="_blank" href={FTC_CAN_SPAM_INFO_LINK} rel="noreferrer">
                            CAN-SPAM Act
                        </a>{' '}
                        and SMS carriers.
                    </p>
                    {tasks.has_address && (
                        <AlertNotification.Root type="success">
                            <AlertNotification.Content>
                                <AlertNotification.Title className="flex items-center gap-1.5">
                                    <FontAwesomeIcon icon={faCheckCircle} />
                                    Successfully added!
                                </AlertNotification.Title>
                            </AlertNotification.Content>
                        </AlertNotification.Root>
                    )}
                    {!tasks.has_address && (
                        <Link
                            className={'flex w-fit items-center gap-1.5'}
                            to={`/accounts/${accountId}/engage/settings/addresses`}
                        >
                            <p>Set up address</p>
                            <FontAwesomeIcon icon={faArrowRight} />
                        </Link>
                    )}
                </div>
            </Box>
        </div>
    );
};

export { GettingStarted };
